/**
 * Position filtering utilities.
 * Pure functions for filtering positions based on filter state.
 */

import type { Position, PositionFilterState } from '@/types';

/**
 * Check if a position matches the symbol filter.
 * Case-insensitive partial match.
 */
export function matchesSymbol(position: Position, symbol: string): boolean {
  if (!symbol) return true;
  return position.symbol.toLowerCase().includes(symbol.toLowerCase());
}

/**
 * Check if a position matches the status filter.
 */
export function matchesStatus(
  position: Position,
  status: PositionFilterState['status']
): boolean {
  if (status === 'all') return true;
  return position.position_status === status;
}

/**
 * Check if a position matches the date range filter.
 */
export function matchesDateRange(
  position: Position,
  filters: Pick<PositionFilterState, 'dateField' | 'startDate' | 'endDate'>
): boolean {
  const { dateField, startDate, endDate } = filters;

  // No date filter applied
  if (!startDate && !endDate) return true;

  // Get the date value based on selected field
  const dateValue =
    dateField === 'created_at' ? position.created_at : position.closed_at;

  // If filtering by closed_at but position has no closed_at, exclude it
  if (dateField === 'closed_at' && !dateValue) {
    return false;
  }

  // If no date value, can't match
  if (!dateValue) return false;

  // Extract date portion (YYYY-MM-DD) from ISO string
  const positionDate = dateValue.split('T')[0] ?? '';

  // Check start date boundary
  if (startDate && positionDate < startDate) {
    return false;
  }

  // Check end date boundary
  if (endDate && positionDate > endDate) {
    return false;
  }

  return true;
}

/**
 * Apply all filters to a position array.
 * Filters are applied with AND logic.
 */
export function applyFilters(
  positions: Position[],
  filters: PositionFilterState
): Position[] {
  return positions.filter((position) => {
    return (
      matchesSymbol(position, filters.symbol) &&
      matchesStatus(position, filters.status) &&
      matchesDateRange(position, filters)
    );
  });
}
